import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { AnalysisStepsState, PpiAnalysisState, initialPpiAnalysisState, analysisSteps as stepDefinitions } from '../../../types';
import { getFirebaseAuth } from '../../../services/firebase';
import { authPpiApiAxiosWithRefresh } from '../../../services/api';
import { PPI_API } from '../../../services/constants';
import { SimilarCar } from '../../../types';

export const PpiAnalysisContext = createContext<PpiAnalysisState>(initialPpiAnalysisState);

export const usePpiAnalysis = () => useContext(PpiAnalysisContext);

interface PpiAnalysisProviderProps {
  children: ReactNode;
  docId: string | null;
}

const getInitialStepsState = (): AnalysisStepsState => {
  const steps: AnalysisStepsState = {};
  stepDefinitions.forEach(step => {
    steps[step.key] = { status: 'pending' };
  });
  return steps;
};

export const PpiAnalysisProvider: React.FC<PpiAnalysisProviderProps> = ({ children, docId }) => {
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisSteps, setAnalysisSteps] = useState<AnalysisStepsState>(getInitialStepsState());
  const [isComplete, setIsComplete] = useState(false);
  const [sentEmailAddress, setSentEmailAddress] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [similarCars, setSimilarCars] = useState<SimilarCar[]>([]);
  const [_taskId, setTaskId] = useState<string | null>(null); // New state for task ID
  const [_statusUrl, setStatusUrl] = useState<string | null>(null); // New state for status URL
  const [_reportAccessUrl, setReportAccessUrl] = useState<string | null>(null); // New state for report access URL

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const resetState = useCallback(() => {
    stopPolling();
    setAnalysisStarted(false);
    setLoading(false);
    setError(null);
    setAnalysisSteps(getInitialStepsState());
    setIsComplete(false);
    setSentEmailAddress(null);
    setEmailError(null);
    setSimilarCars([]);
    setTaskId(null); // Reset task ID
    setStatusUrl(null); // Reset status URL
    setReportAccessUrl(null); // Reset report access URL
  }, [stopPolling]);

  const pollTaskStatus = useCallback((taskId: string, statusUrl: string, token: string) => {
    // Stop any existing poll before starting a new one
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    const fetchStatus = async () => {
      try {
        const response = await fetch(`${PPI_API}${statusUrl}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch task status.');
        }

        const responseJson = await response.json();
        // The backend wraps the task data in { status: "success", data: { ... } }
        const data = responseJson.data ?? responseJson;
        const { status, analysis_steps, email, error: taskError, report_access_url, message: msg } = data;

        if (analysis_steps && Object.keys(analysis_steps).length > 0) {
          // Merge into existing state — mid-run responses only contain completed steps,
          // so replacing wholesale would wipe still-pending steps (e.g. market_insights)
          setAnalysisSteps(prev => ({ ...prev, ...analysis_steps }));
        }

        if (status === 'completed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsComplete(true);
          setLoading(false);
          setSentEmailAddress(email || null);
          setReportAccessUrl(report_access_url || null);
        } else if (status === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsComplete(true);
          setLoading(false);
          setError(taskError || 'Analysis failed.');
          setAnalysisSteps(getInitialStepsState());
          setAnalysisStarted(false);
        } else if (status === 'partial_success') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsComplete(true);
          setLoading(false);
          setEmailError(msg || 'Email send failed.');
          setReportAccessUrl(report_access_url || null); // Still provide report if partially successful
        }
        // If still in_progress, continue polling
      } catch (err: any) {
        console.error('Polling error:', err);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setError(err.message || 'An error occurred during polling.');
        setIsComplete(true);
        setLoading(false);
        setAnalysisSteps(getInitialStepsState());
        setAnalysisStarted(false);
      }
    };

    // Start polling — ref is set first so clearInterval works in fetchStatus from the first call onward
    pollIntervalRef.current = setInterval(fetchStatus, 3000);
    fetchStatus(); // Immediate first fetch

  }, [setAnalysisSteps, setLoading, setIsComplete, setError, setSentEmailAddress, setEmailError, setAnalysisStarted, setReportAccessUrl]);

  const fetchSimilarCars = useCallback(async () => {
    if (docId) { // Check if docId is available
      try {
        const similarCarsResponse = await (await authPpiApiAxiosWithRefresh()).get(`/getSimilarSold/${docId}`);
        setSimilarCars(similarCarsResponse.data);
      } catch (err) {
        console.error("Failed to fetch similar cars:", err);
      }
    }
  }, [docId]);

  const loadExistingAnalysis = useCallback(async () => {
    if (!docId) return;

    setLoading(true);
    try {
      const response = await (await authPpiApiAxiosWithRefresh()).get(`/get-analysis-data/${docId}`);
      const data = await response.data;

      if (data.success) {
        setAnalysisSteps(data.analysisSteps);
        setSentEmailAddress(data.email || null);

        // Fetch similar cars as soon as vehicle details are available from loaded analysis
        fetchSimilarCars();

        if (data.overallStatus === 'completed' || data.overallStatus === 'error') {
          setIsComplete(true);
          setAnalysisStarted(false);
        } else if (data.overallStatus === 'in_progress') {
          setAnalysisStarted(true);
          setIsComplete(false);
        } else {
          const hasStarted = Object.values(data.analysisSteps).some((s: any) => s.status !== 'pending');
          setAnalysisStarted(hasStarted);
          setIsComplete(false);
        }

      } else {
        setError(data.error || 'Failed to load existing analysis.');
      }
    } catch (err) {
      setError('Error loading existing analysis.');
      console.error('Error loading existing analysis:', err);
    } finally {
      setLoading(false);
    }
  }, [docId, fetchSimilarCars]);

  useEffect(() => {
    if (docId) {
      loadExistingAnalysis();
    }
  }, [docId, loadExistingAnalysis]);

  const startAnalysis = useCallback(async (sendEmail: boolean) => {
    if (!docId) {
      setError('docId is not available.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisStarted(true);
    setAnalysisSteps(prev => {
      const newSteps = { ...prev };
      stepDefinitions.forEach(step => {
        newSteps[step.key] = { status: 'in_progress' };
      });
      return newSteps;
    });

    try {
      const currentUser = getFirebaseAuth().currentUser;
      if (!currentUser) throw new Error('No authenticated user found');
      const token = await currentUser.getIdToken();

      // Fetch similar cars in parallel with the analysis stream
      fetchSimilarCars();

      const response = await fetch(`${PPI_API}/api/create-ppi-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ doc_id: docId, sendEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start analysis.');
      }

      const { task_id, status_url } = await response.json();

      if (!task_id || !status_url) {
        throw new Error('Task ID or status URL not received from the server.');
      }

      setTaskId(task_id); // Store task ID in state
      setStatusUrl(status_url); // Store status URL in state

      // Start polling for task status
      pollTaskStatus(task_id, status_url, token);
    } catch (err: any) {
      setError(err.message || 'An error occurred with the analysis stream.');
      setAnalysisSteps(getInitialStepsState());
      setAnalysisStarted(false);
      // Reload existing analysis data so user can see previous run if available
      loadExistingAnalysis();
    } finally {
      // setLoading(false); // Do not set loading to false here, polling will handle it
    }
  }, [docId, fetchSimilarCars, loadExistingAnalysis, pollTaskStatus]);

  const value = {
    analysisStarted,
    loading,
    error,
    analysisSteps,
    isComplete,
    sentEmailAddress,
    emailError,
    similarCars,
    task_id: _taskId, // Expose task ID
    status_url: _statusUrl, // Expose status URL
    report_access_url: _reportAccessUrl, // Expose report access URL
    startAnalysis,
    resetState,
  };

  return (
    <PpiAnalysisContext.Provider value={value}>
      {children}
    </PpiAnalysisContext.Provider>
  );
};